/* eslint-disable no-control-regex */

import StepLibData from '@/models/StepLibData';

type StepInfo = null | {
  footerLines?: string[];
  headerFirstLine?: string;
  iconURL?: string;
  issueTracker?: string;
  isUpdateAvailable?: boolean;
  workflowName?: string;
  stepStatus?: string;
  stepSource?: string;
  stepId?: string;
  stepVersion?: string;
  stepTitle?: string;
};

export type FooterMatchResult = {
  regexPatterns: RegExp[];
  isMatch: boolean;
  isFullMatch: (index: number) => boolean;
  footerLines: string[];
};

export type GroupOfLines = {
  stepInfo: StepInfo;
  lines: string[];
};

type LatestStepVersions = {
  [stepId: string]: string;
};

type StepIconURLs = {
  [stepId: string]: {
    [stepVersion: string]: string;
  };
};

export type GetStepsDataReturn = {
  latestStepVersions: LatestStepVersions;
  stepIconURLs: StepIconURLs;
};

const regexpForStepHeaderFirstLine = /^\|\s\(\d+\).+\|$/;
const regexpForStepTitle = /^\|\s\(\d+\)\s(.*?)\s+\|$/;
const regexpForStepHeaderSecondLine = /^\+-{78}\+$/;
const regexpForStepIdLine = /^\|\sid:\s([^\s]+)\s*\|$/;
const regexpForStepVersionLine = /^\|\sversion:\s([^\s]*)\s+\|$/;
const regexpForStepCollectionLine = /^\|\scollection:\s([^\s]+)\s+\|$/;
const regexpForStepFooterStatusLine = /^\|\s+\[\d{2};\d{1}m([✓\-x!]).+\|.+\|\s[\d.]+.+\|$/;
const regexpForStepFooterIssueTrackerLine = /^\|\sIssue\stracker:\s([^\s]+)\s*\|$/;
const regexpForStepFooterStepSourceLine = /^\|\sSource:\s([^\s]+)\s+\|$/;
const regexpForStepFooterSeparatorLine = /^\+-{3}\+-{63}\+-{10}\+$/;
const regexpForTotalRuntimeLine = /^\|\sTotal\sruntime:\s[\d.]+\s(?:sec|min|hour)\s+\|$/;
const regexpForLocalPathSource = /path::/;
const regexpForAnything = /[\s\S]*/;
const regexpForUpdateAvailableLine = /\s*\|\s*Update available:\s*(.*)\|\s*/;
const regexpForReleaseNotesLine = /\s*\|\s*Release notes are available below\s*\|\s*/;

const regexpsForStepHeader = [
  regexpForStepHeaderFirstLine,
  regexpForStepHeaderSecondLine,
  regexpForStepIdLine,
  regexpForStepVersionLine,
  regexpForStepCollectionLine,
];

export const regexpsForStepFooter = [
  regexpForStepFooterSeparatorLine,
  regexpForStepFooterStatusLine,
  regexpForStepFooterSeparatorLine,
];

export const regexpsForErrorStepFooter = regexpsForStepFooter.concat([
  regexpForStepFooterIssueTrackerLine,
  regexpForStepFooterStepSourceLine,
  regexpForStepFooterSeparatorLine,
]);

export const regexpsForStepFooterWithUpdate = regexpsForStepFooter.concat([
  regexpForUpdateAvailableLine,
  regexpForAnything,
  regexpForReleaseNotesLine,
  regexpForAnything,
  regexpForStepFooterSeparatorLine,
]);

export const regexpsForErrorStepFooterWithUpdate = regexpsForStepFooter.concat([
  regexpForUpdateAvailableLine,
  regexpForAnything,
  regexpForReleaseNotesLine,
  regexpForAnything,
  regexpForStepFooterIssueTrackerLine,
  regexpForStepFooterStepSourceLine,
  regexpForStepFooterSeparatorLine,
]);

export const PossibleFooterMatchers = [
  regexpsForErrorStepFooterWithUpdate,
  regexpsForStepFooterWithUpdate,
  regexpsForErrorStepFooter,
  regexpsForStepFooter,
];

export const getStepsData = (stepLibData: StepLibData): GetStepsDataReturn => {
  const latestStepVersions: LatestStepVersions = {};
  const stepIconURLs: StepIconURLs = {};

  Object.keys(stepLibData.steps).forEach((stepId) => {
    const step = stepLibData.steps[stepId];
    stepIconURLs[stepId] = {};
    Object.keys(step.versions).forEach((v, index) => {
      if (index === 0) {
        latestStepVersions[stepId] = v;
      }
      const stepVersion = step.versions[v];
      if (stepVersion.asset_urls) {
        stepIconURLs[stepId][v] = stepVersion.asset_urls['icon.svg'] || stepVersion.asset_urls['icon.png'];
      }
    });
  });

  return {
    latestStepVersions,
    stepIconURLs,
  };
};

export const parseAndSelectTitle = (
  actualLine: string,
  stepLibData: StepLibData,
  stepId: string,
  stepVersion?: string,
  latestVersion?: string,
): string => {
  const trimmedStepId = stepId.trim();
  const trimmedStepVersion = stepVersion?.trim();
  const trimmedLatestVersion = latestVersion?.trim();

  const versionedStepIdPattern = RegExp(`${trimmedStepId}@`);
  let defaultTitle: string | undefined;
  const logLineTitle = regexpForStepTitle.exec(actualLine)?.[1] || actualLine || trimmedStepId;
  const isStepIdLikeTitle =
    !!stepId && !!logLineTitle && (logLineTitle === stepId || versionedStepIdPattern.test(logLineTitle));

  if (isStepIdLikeTitle && trimmedStepId) {
    defaultTitle = selectDefaultStepTitle(stepLibData, trimmedStepId, trimmedStepVersion, trimmedLatestVersion);
  }

  return defaultTitle || logLineTitle || 'Unknown step title';
};

export const selectDefaultStepTitle = (
  stepLibData: StepLibData,
  stepId: string,
  stepVersion?: string,
  latestVersion?: string,
): string | undefined => {
  let defaultTitle: string | undefined;
  const trimmedStepId = stepId.trim();
  const trimmedStepVersion = stepVersion?.trim();
  const trimmedLatestVersion = latestVersion?.trim();

  const stepVersions = stepLibData?.steps[trimmedStepId]?.versions;

  if (stepVersions) {
    // Latest step version
    if (!defaultTitle && trimmedLatestVersion) {
      defaultTitle = stepVersions[trimmedLatestVersion]?.title;
    }

    // Current step version
    if (!defaultTitle && trimmedStepVersion) {
      defaultTitle = stepVersions[trimmedStepVersion]?.title;
    }

    // Any step version
    if (!defaultTitle) {
      const firstStepVersionKey = Object.keys(stepVersions)[0];
      defaultTitle = stepVersions[firstStepVersionKey]?.title;
    }
  }

  return defaultTitle;
};

export const isContinuousMatch = (regexArray: RegExp[], linesToMatch: string[]) =>
  regexArray.every((regexp, idx) => regexp && linesToMatch && regexp.test(linesToMatch[idx]));

const toFooterMatchResult = (
  lines: string[],
  startOfFooter: number,
): ((regexPatterns: RegExp[]) => FooterMatchResult) => {
  return (regexPatterns: RegExp[]) => {
    const endOfFooter = startOfFooter + regexPatterns.length;
    const footerLines = lines.slice(startOfFooter, endOfFooter);
    const isMatch = isContinuousMatch(regexPatterns, footerLines);
    return {
      isMatch,
      footerLines,
      regexPatterns,
      isFullMatch: (idx: number) => idx === endOfFooter,
    };
  };
};

export const selectFooterMatcher = (
  possibleMatchers: Array<RegExp[]>,
  lines: string[],
  statusLineIndex: number,
): FooterMatchResult | undefined => {
  // Footer matchers are ordered descending by length
  const matcherResults = possibleMatchers
    .sort((a, b) => b.length - a.length)
    .map(toFooterMatchResult(lines, statusLineIndex - 1));
  // Selecting the first footer matcher which will fully match (the longest matching)
  return matcherResults.find((matcher) => matcher.isMatch);
};

export const parseFooterLine = (footerMatcher: FooterMatchResult, index: number): string | undefined => {
  const regexp = footerMatcher.regexPatterns[index];
  return regexp ? regexp.exec(footerMatcher.footerLines[index])?.[1] : undefined;
};

export const groupLog = (logString: string, stepLibData: StepLibData): GroupOfLines[] => {
  const { latestStepVersions, stepIconURLs } = getStepsData(stepLibData);
  const lines = logString.split('\n');
  const groupOfLines: GroupOfLines[] = [
    {
      stepInfo: null,
      lines: [],
    },
  ];
  let currentStepInfo: StepInfo = null;
  // E. g. if a step itself contains a "nested" log, during that the current depth is one level deeper. If that contains another "nested" log, two level deep, etc.
  let currentNestedLogDepth = 0;

  lines.forEach((actualLine, index) => {
    if (currentStepInfo === null) {
      // If line is not part of a step's log yet
      const potentialStepHeaderLines = lines.slice(index, index + regexpsForStepHeader.length);
      const isStepHeader = [0, regexpsForStepHeader.length - 1].every((fooIndex) => {
        if (potentialStepHeaderLines[fooIndex] === undefined) {
          return false;
        }

        return regexpsForStepHeader[fooIndex].test(potentialStepHeaderLines[fooIndex]);
      });

      if (isStepHeader) {
        // If line is first line of a new step's header...
        let iconURL;
        let isUpdateAvailable = false;
        let stepId;
        let stepVersion;

        const isLocalPath = regexpForLocalPathSource.test(potentialStepHeaderLines[0]);
        if (!isLocalPath) {
          stepId = regexpForStepIdLine.exec(
            potentialStepHeaderLines[regexpsForStepHeader.indexOf(regexpForStepIdLine)],
          )?.[1];
          stepVersion = regexpForStepVersionLine.exec(
            potentialStepHeaderLines[regexpsForStepHeader.indexOf(regexpForStepVersionLine)],
          )?.[1];
          const stepCollection = regexpForStepCollectionLine.exec(
            potentialStepHeaderLines[regexpsForStepHeader.indexOf(regexpForStepCollectionLine)],
          )?.[1];

          if (stepCollection === 'https://github.com/bitrise-io/bitrise-steplib.git') {
            try {
              iconURL = stepId ? stepIconURLs[stepId][latestStepVersions[stepId]] : undefined;
            } catch {
              iconURL = undefined;
            }
          }

          isUpdateAvailable = !!latestStepVersions && !!stepId && stepVersion !== latestStepVersions[stepId];
        }

        let stepTitle = actualLine;
        const titleMatch = actualLine.match(regexpForStepTitle);
        if (titleMatch) {
          stepTitle = titleMatch[1];
        }

        currentStepInfo = {
          headerFirstLine: actualLine,
          iconURL,
          isUpdateAvailable,
          stepId,
          stepVersion,
          stepTitle,
        };

        groupOfLines.push({
          // ...then start a new group of lines, which belong to this new step
          stepInfo: currentStepInfo,
          lines: [],
        });
      }
    } else {
      // If line is part of a step's log
      if (actualLine === '  ██████╗ ██╗████████╗██████╗ ██╗███████╗███████╗') {
        // If line is a nested log's first line, we are going one level deeper to the logs
        currentNestedLogDepth += 1;
      }

      if (currentNestedLogDepth === 0) {
        // If all nested logs are finished, we can start checking for the step's footer line
        const linesByLastStep = groupOfLines[groupOfLines.length - 1];

        linesByLastStep.lines.push(actualLine);

        // If it's not the status of the footer then don't do anything
        const statusLineMatches = regexpForStepFooterStatusLine.exec(actualLine);
        if (statusLineMatches && statusLineMatches.length > 0) {
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          if (statusLineMatches[1] === 'x' || statusLineMatches[1] === '!') {
            linesByLastStep.stepInfo!.stepStatus = 'error';
          } else if (statusLineMatches[1] === '✓') {
            linesByLastStep.stepInfo!.stepStatus = 'success';
          } else if (statusLineMatches[1] === '-') {
            linesByLastStep.stepInfo!.stepStatus = 'skipped';
          } else {
            linesByLastStep.stepInfo!.stepStatus = 'undefined';
          }
          /* eslint-enable @typescript-eslint/no-non-null-assertion */
        }

        if (linesByLastStep.stepInfo?.stepStatus) {
          const regexpsForFooterCheck =
            linesByLastStep.stepInfo.stepStatus === 'error' ? regexpsForErrorStepFooter : regexpsForStepFooter;
          const indexOfFirstLineOfFooter = linesByLastStep.lines.findIndex((actualLineByLastStep) => {
            return regexpForStepFooterSeparatorLine.test(actualLineByLastStep);
          });
          const potentialStepFooterLines = linesByLastStep.lines.slice(
            indexOfFirstLineOfFooter,
            indexOfFirstLineOfFooter + regexpsForFooterCheck.length,
          );
          const isStepFooter = [0, regexpsForFooterCheck.length - 1].every((fooIndex) => {
            if (potentialStepFooterLines[fooIndex] === undefined) {
              return false;
            }

            return regexpsForFooterCheck[fooIndex].test(potentialStepFooterLines[fooIndex]);
          });

          if (isStepFooter) {
            if (currentStepInfo === null) {
              currentStepInfo = {};
            }
            // If step contains the whole footer
            if (linesByLastStep.stepInfo.stepStatus === 'error') {
              currentStepInfo.issueTracker = regexpForStepFooterIssueTrackerLine.exec(
                potentialStepFooterLines[regexpsForFooterCheck.indexOf(regexpForStepFooterIssueTrackerLine)],
              )?.[1];
              currentStepInfo.stepSource = regexpForStepFooterStepSourceLine.exec(
                potentialStepFooterLines[regexpsForFooterCheck.indexOf(regexpForStepFooterStepSourceLine)],
              )?.[1];
            }
            currentStepInfo.footerLines = potentialStepFooterLines;

            groupOfLines.push({
              // ...then after adding this line to the step's lines, finish this step's group and start a new group, without step
              stepInfo: null,
              lines: [],
            });

            currentStepInfo = null;

            return;
          }
        }

        return;
      }
      // If we're in a middle of a nested log, we should check whether it has finished
      if (regexpForTotalRuntimeLine.test(actualLine)) {
        // If a nested log has finished, we are one level less deep
        currentNestedLogDepth -= 1;
      }
    }

    groupOfLines[groupOfLines.length - 1].lines.push(actualLine);
  });

  return groupOfLines;
};
